<template>
  <div class="layer-main-wrapper">
    <div class="layers-heading">
      <span class="panel-kicker">Layers</span>
    </div>
    <draggable
        v-model="layerList"
        class="layer-list"
        :group="dragGroupOptions"
        :animation="150"
        @start="onTopDragStart"
        @end="onDragEnd"
        @mousedown.native="prepareDragPreview"
        @dragstart.native="useDragPreviewAsDragImage"
    >
      <layer-row
          v-for="layer in layerList"
          v-if="layer.type !== 'pagedetails'"
          :key="layer.Id"
          :layer="layer"
          :depth="0"
          :drag-group-options="dragGroupOptions"
          :dragging-layer-ids="draggingLayerIds"
          @drag-start="onChildDragStart"
          @drag-end="onChildDragEnd"
          @select="selectLayer"
          @contextmenu="(event, layer, parentGroup, isChild) => openContextMenu(event, layer, parentGroup, isChild)"
          @toggle-visibility="toggleVisibility"
          @duplicate="duplicateLayer"
          @lock="lockLayer"
          @delete="handleDeleteLayer"
          @validate-input="validateInput"
          @toggle-group="toggleGroupExpanded"
      />
    </draggable>
    <div
        v-if="getProductAudio"
        class="layer-audio-section"
    >
      <div class="layer-section-divider" />
      <div class="layer-list">
        <div
            v-for="(layer,index) in getProductAudio"
            :key="index"
            :class="{ 'active-layer': isAudioLayerSelected, 'layer-wrapper--renaming': isAudioLayerEditableForRightPanel }"
            class="layer-wrapper"
            @click.stop="selectAudioLayer">
          <span class="layer-chevron-slot" />
          <span class="layer-type-icon">
            <layer-icon
                name="audio"
                :size="15"/>
          </span>
          <input
              v-model.trim="layer.Name"
              :readonly="!isAudioLayerEditableForRightPanel"
              :class="{ 'layer-input--editing': isAudioLayerEditableForRightPanel }"
              class="layer-input"
              type="text"
              aria-label="Layer name"
              @dblclick="makeAudioLayerEditableForRightPanel"
              @click.stop="selectAudioLayer"
              @blur.stop="updateAudioLayerName(layer.Name, $event)"
              @keyup.enter="$event.target.blur()"
          >
          <div class="layer-actions">
            <button
                type="button"
                class="layer-action"
                aria-label="Delete audio"
                title="Delete audio"
                @click.stop="removeAudioFromProduct">
              <layer-icon
                  name="trash"
                  :size="14"/>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
        ref="dragPreview"
        class="layer-drag-preview"
        aria-hidden="true"
    >
      <div class="layer-drag-preview__back" />
      <div class="layer-drag-preview__mid" />
      <div class="layer-drag-preview__front">
        <layer-icon
            :name="dragPreview.icon"
            :size="15"/>
        <span class="layer-drag-preview__name">{{ dragPreview.name }}</span>
        <span
            v-if="dragPreview.count"
            class="layer-drag-preview__count"
        >{{ dragPreview.count }}</span>
      </div>
    </div>

    <layer-context-menu
        ref="contextMenu"
        :visible="contextMenu.visible"
        :x="contextMenu.x"
        :y="contextMenu.y"
        :items="contextMenuItems"
        @select="onContextMenuSelect"
    />
  </div>
</template>
<script>
import { uuid } from 'vue-uuid';
import draggable from 'vuedraggable';
import _ from 'lodash';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import {
  reassignGroupIds,
  canAddLayersToGroup,
  addLayersToExistingGroup,
  recalculateGroupBounds,
  replaceLayersWithGroup,
  getUniqueGroupName,
  getNextTableName,
  clearAllSelection,
} from '@/components/Canvas/utils/groupHelpers';
import { createGroupFromObjects } from '@/components/DesignObjects/GroupObject';
import LayerRow from '@/components/Layer/LayerRow.vue';
import LayerContextMenu from '@/components/Layer/LayerContextMenu.vue';
import LayerIcon from '@/components/Layer/LayerIcon.vue';
import { getLayerIconName } from '@/components/Layer/layerIcons';

export default {
  components: {
    draggable,
    LayerRow,
    LayerContextMenu,
    LayerIcon,
  },

  props: {
    product: {
      type: Object,
    },
    contentListReceive: Array,
    activePage:         Number,
  },

  data() {
    return {
      layerList:                         [],
      isDragged:                         false,
      isAudioLayerSelected:              false,
      isAudioLayerEditableForRightPanel: false,
      contextMenu:                       {
        visible:     false,
        x:           0,
        y:           0,
        layer:       null,
        parentGroup: null,
        isChild:     false,
      },
      dragContext: {
        fromGroup: null,
        layerId:   null,
        // Ids of every selected layer, at any depth, in stack order at drag start
        selectedIds: [],
      },
      // Name and layer count shown on the drag image of a multi-selection
      dragPreview: {
        name:  '',
        count: 0,
        icon:  'image',
      },
      // Id of the first layer chosen in the current multi-selection
      selectionAnchorId: null,
    };
  },
  computed: {
    draggingLayerIds() {
      if (!this.isMultiSelectionDrag()) { return []; }
      const { layerId, selectedIds } = this.dragContext;
      return selectedIds.filter(id => id !== layerId);
    },
    dragGroupOptions() {
      return {
        name: 'layers',
        pull: true,
        put:  (to, from, dragEl) => {
          if (!to.el.classList.contains('layer-group-children')) {
            return true;
          }
          const layers = this.getDraggedLayers(this.getLayerIdFromDragEl(dragEl));
          return layers.length > 0 && layers.every(layer => layer.type !== 'group');
        },
      };
    },
    getProductAudio() {
      if (this.product && this.product.audioDetails && this.product.audioDetails.audioUrl) {
        const audioLayer = [{
          Name: this.product.audioDetails.Name,
        }];
        return audioLayer;
      }
      return 0;
    },
    getActivePageIndex() {
      return this.activePage;
    },
    contextMenuItems() {
      const { layer, isChild, parentGroup } = this.contextMenu;
      if (!layer) { return []; }

      const items = [
        {
          id: 'duplicate',
          label: 'Duplicate',
          icon: 'duplicate'
        },
        {
          id: 'visibility',
          label: layer.IsVisible ? 'Hide' : 'Show',
          icon: layer.IsVisible ? 'eye-off' : 'eye'
        },
        {
          id: 'lock',
          label: layer.locked ? 'Unlock' : 'Lock',
          icon: layer.locked ? 'lock' : 'lock-open'
        },
        {
          id: 'delete',
          label: 'Delete',
          icon: 'trash'
        },
      ];

      if (isChild) {
        items.push({ id: 'divider-remove', divider: true });
        items.push({
          id:       'remove-from-group',
          label:    'Remove from group',
          disabled: layer.locked,
          icon: 'remove-group'
        });
        return items;
      }

      if (layer.type === 'group') {
        items.push({
          id:       'ungroup',
          label:    'Ungroup',
          icon: 'remove-group',
          disabled: false,
        });

        const selectedTopLevel = this.getSelectedTopLevelLayers().filter(item => item.Id !== layer.Id);
        if (selectedTopLevel.length) {
          items.push({
            id:       'add-to-this-group',
            label:    'Add selection to group',
            disabled: !canAddLayersToGroup(selectedTopLevel),
            icon: 'folder'
          });
        }
        return items;
      }

      const selectedGroup = this.getSelectedGroupLayer();
      if (selectedGroup) {
        const selectedTopLevel = this.getSelectedTopLevelLayers();
        items.push({
          id:       'add-to-group',
          label:    'Add to group',
          disabled: !canAddLayersToGroup(selectedTopLevel),
          icon: 'folder'
        });
        return items;
      }

      items.push({
        id:       'add-to-group',
        label:    'Add to group',
        disabled: !canAddLayersToGroup(this.getLayersForGrouping(layer)),
        icon: 'folder'
      });

      return items;
    },
  },
  watch: {
    contentListReceive: {
      immediate: true,
      handler(newValue) {
        this.layerList = newValue;
      },
    },
  },
  mounted() {
    EventBus.$on('selectAudioLayerFromLeftSection', this.selectAudioLayerFromLeftSection);
    EventBus.$on('unSelectAudioLayer', this.unSelectAudioLayer);
    EventBus.$on(ApplicationEventTypes.ObjectSelectionEvents.REMOVE_FROM_GROUP, this.removeFromGroup);
    document.addEventListener('mousedown', this.handleOutsideClick);
    document.addEventListener('contextmenu', this.onDocumentContextMenu);
  },
  destroyed() {
    EventBus.$off('selectAudioLayerFromLeftSection', this.selectAudioLayerFromLeftSection);
    EventBus.$off('unSelectAudioLayer', this.unSelectAudioLayer);
    EventBus.$off(ApplicationEventTypes.ObjectSelectionEvents.REMOVE_FROM_GROUP, this.removeFromGroup);
    document.removeEventListener('mousedown', this.handleOutsideClick);
    document.removeEventListener('contextmenu', this.onDocumentContextMenu);
  },
  methods: {
    handleOutsideClick(e) {
      if (!this.contextMenu.visible) {
        return;
      }
      const menu = this.$refs.contextMenu.$el;
      if (menu && !menu.contains(e.target)) {
        this.closeContextMenu();
      }
    },
    onTopDragStart(evt) {
      this.dragContext = {
        fromGroup: null,
        layerId:   this.getLayerIdFromDragEvent(evt),
        selectedIds: this.getSelectedLayerIds(),
      };
    },
    onChildDragStart({ group, event }) {
      this.dragContext = {
        fromGroup: group,
        layerId:   this.getLayerIdFromDragEvent(event),
        selectedIds: this.getSelectedLayerIds(),
      };
    },
    handleCrossListDrop(evt) {
      const droppedInGroup   = evt.to.classList.contains('layer-group-children');
      const draggedFromGroup = evt.from.classList.contains('layer-group-children');
      const sameContainer    = evt.from === evt.to;
      const layerId          = this.dragContext.layerId;
      if (!layerId) { return; }

      // CASE A: top-level layer dropped INTO a group
      if (!draggedFromGroup && droppedInGroup) {
        const targetGroup = this.findGroupByChildrenContainer(evt.to);
        const layer       = this.findLayerInGroup(targetGroup, layerId);
        if (targetGroup && layer && layer.type !== 'group') {
          this.normalizeLayerAddedToGroup(targetGroup, layer);
          this.$set(targetGroup, 'isGroupExpanded', true);
        }
      }

      // CASE B: child dropped OUT to top-level
      if (draggedFromGroup && !droppedInGroup) {
        const fromGroup = this.findGroupInList(this.dragContext.fromGroup);
        const layer     = this.findTopLevelLayer(layerId);
        if (fromGroup && layer) {
          this.updateGroupAfterChildRemoved(fromGroup);
        }
      }

      // CASE E: child moved between two different groups
      if (draggedFromGroup && droppedInGroup && !sameContainer) {
        const fromGroup   = this.findGroupInList(this.dragContext.fromGroup);
        const targetGroup = this.findGroupByChildrenContainer(evt.to);
        const layer       = this.findLayerInGroup(targetGroup, layerId);
        if (fromGroup && targetGroup && fromGroup.Id !== targetGroup.Id && layer) {
          this.normalizeLayerAddedToGroup(targetGroup, layer);
          this.updateGroupAfterChildRemoved(fromGroup);
        }
      }

      // CASE C/D: reorder inside same group or top-level — no coord changes
      this.dragContext = { fromGroup: null, layerId: null, selectedIds: [] };
      this.syncLayerChanges('layer drag');
    },
    findGroupByChildrenContainer(containerEl) {
      // Find group row id, then find that group anywhere in the hierarchy
      const layerRow = containerEl && containerEl.closest('.layer-row-block');
      const groupRow = layerRow && layerRow.querySelector('[id$="#layerId"]');
      if (!groupRow) { return null; }
      return this.findGroupByIdAnywhere(groupRow.id.replace('#layerId', ''));
    },
    findTopLevelLayer(layerId) {
      return this.layerList.find(item => item.Id === layerId);
    },
    findGroupInList(groupRef) {
      if (!groupRef) { return null; }
      return this.findGroupByIdAnywhere(groupRef.Id);
    },
    findLayerInGroup(group, layerId) {
      if (!group || !group.children) { return null; }
      return group.children.find(child => child.Id === layerId) || null;
    },
    normalizeLayerAddedToGroup(group, layer) {
      // Children use canvas-absolute coords — do not subtract group origin.
      layer.isSelected = false;
      this.recalculateGroupBounds(group);
    },
    recalculateGroupBounds(group) {
      recalculateGroupBounds(group);
    },
    updateGroupAfterChildRemoved(group) {
      const location = this.findLayerLocation(group.Id);
      if (!location) { return; }

      if (!group.children || !group.children.length) {
        location.list.splice(location.index, 1);
        return;
      }

      if (group.children.length === 1) {
        const remaining = group.children[0];
        // Already absolute — promote as-is.
        location.list.splice(location.index, 1, remaining);
        return;
      }

      this.recalculateGroupBounds(group);
    },
    getLayerIdFromDragEvent(evt) {
      const el = evt.item.querySelector('[id$="#layerId"]');
      return el ? el.id.replace('#layerId', '') : null;
    },
    getLayerIdFromDragEl(dragEl) {
      const el = dragEl && dragEl.querySelector('[id$="#layerId"]');
      return el ? el.id.replace('#layerId', '') : null;
    },
    findLayerLocation(layerId, list = this.layerList, parent = null) {
      for (let i = 0; i < list.length; i += 1) {
        const item = list[i];
        if (item.Id === layerId) { return { list, index: i, parent }; }
        if (item.type === 'group' && item.children) {
          const nested = this.findLayerLocation(layerId, item.children, item);
          if (nested) { return nested; }
        }
      }
      return null;
    },
    findLayerByIdAnywhere(layerId) {
      const location = this.findLayerLocation(layerId);
      return location ? location.list[location.index] : null;
    },
    findGroupByIdAnywhere(groupId) {
      const layer = groupId ? this.findLayerByIdAnywhere(groupId) : null;
      return layer && layer.type === 'group' ? layer : null;
    },
    prepareDragPreview(event) {
      const row         = event.target.closest('.layer-wrapper');
      const layerId     = row ? row.id.replace('#layerId', '') : null;
      const layer       = layerId ? this.findLayerByIdAnywhere(layerId) : null;
      const selectedIds = this.getSelectedLayerIds();

      this.dragPreview = (layer && selectedIds.length > 1 && selectedIds.includes(layerId))
          ? { name: layer.Name, count: selectedIds.length, icon: getLayerIconName(layer) }
          : { name: '', count: 0, icon: 'image' };
    },
    useDragPreviewAsDragImage(event) {
      if (this.dragPreview.count && event.dataTransfer) {
        event.dataTransfer.setDragImage(this.$refs.dragPreview, 16, 16);
      }
    },
    onDragEnd(evt) {
      this.dragPreview = { name: '', count: 0, icon: 'image' };

      if (this.tryAbsorbDropIntoGroup(evt)) {
        return;
      }
      if (this.moveSelectionWithDraggedLayer()) {
        this.dragContext = { fromGroup: null, layerId: null, selectedIds: [] };
        this.syncLayerChanges('multi-selection layer drag');
        return;
      }
      this.handleCrossListDrop(evt);
    },
    onChildDragEnd({ event }) {
      this.onDragEnd(event);
    },
    getSelectedLayers(list = this.layerList) {
      return list.reduce((selected, item) => {
        if (item.type === 'pagedetails') { return selected; }
        if (item.isSelected) { return selected.concat(item); }
        if (item.type === 'group' && item.children) {
          return selected.concat(this.getSelectedLayers(item.children));
        }
        return selected;
      }, []);
    },
    getSelectedLayerIds(list = this.layerList) {
      return this.getSelectedLayers(list).map(item => item.Id);
    },
    isMultiSelectionDrag() {
      const { layerId, selectedIds } = this.dragContext;
      return !!layerId && selectedIds.length > 1 && selectedIds.includes(layerId);
    },
    getLayersForAction(layer) {
      const selected = this.getSelectedLayers();
      return (selected.length > 1 && selected.some(item => item.Id === layer.Id))
          ? selected
          : [layer];
    },
    getParentGroupsOf(layers) {
      return layers.reduce((groups, layer) => {
        const location = this.findLayerLocation(layer.Id);
        const parent   = location && location.parent;
        if (parent && !groups.includes(parent)) { groups.push(parent); }
        return groups;
      }, []);
    },
    emitSelectionToCanvas(layers) {
      if (layers.length > 1) {
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS, {
          objects:           layers,
          anchorId:          this.selectionAnchorId,
          keepGroupChildren: true,
        });
      } else if (layers.length === 1) {
        EventBus.$emit('onObjectSelectedFromRightPanel', layers[0].Id);
      }
    },
    getDraggedLayers(layerId = this.dragContext.layerId) {
      const { selectedIds } = this.dragContext;
      const draggedIds      = selectedIds.includes(layerId) ? selectedIds : [layerId];
      return draggedIds.map(id => this.findLayerByIdAnywhere(id)).filter(Boolean);
    },
    moveSelectionWithDraggedLayer() {
      if (!this.isMultiSelectionDrag()) { return false; }

      const { layerId, selectedIds } = this.dragContext;
      const dropTarget               = this.findLayerLocation(layerId);
      if (!dropTarget) { return false; }

      // Drop position = how many unselected layers ended up above the dragged row
      const movedIds      = new Set(selectedIds);
      const insertAtIndex = dropTarget.list
          .slice(0, dropTarget.index)
          .filter(item => !movedIds.has(item.Id)).length;

      // Lift the whole selection out of wherever each layer lives right now
      const movedLayers  = [];
      const sourceGroups = [];
      selectedIds.forEach((id) => {
        const location = this.findLayerLocation(id);
        if (!location) { return; }
        movedLayers.push(location.list.splice(location.index, 1)[0]);
        if (location.parent && location.parent !== dropTarget.parent
            && !sourceGroups.includes(location.parent)) {
          sourceGroups.push(location.parent);
        }
      });

      dropTarget.list.splice(insertAtIndex, 0, ...movedLayers);

      // Children keep canvas-absolute coords, so only the bounds need redoing.
      if (dropTarget.parent) {
        this.$set(dropTarget.parent, 'isGroupExpanded', true);
        this.recalculateGroupBounds(dropTarget.parent);
      }
      sourceGroups.forEach(group => this.updateGroupAfterChildRemoved(group));
      return true;
    },
    tryAbsorbDropIntoGroup(evt) {
      if (!evt || (evt.to && evt.to.classList.contains('layer-group-children'))) {
        return false;
      }
      const oe = evt.originalEvent;
      if (!oe || typeof oe.clientX !== 'number') {
        return false;
      }

      const hitEl = document.elementFromPoint(oe.clientX, oe.clientY);
      if (!hitEl) {
        return false;
      }

      const groupWrapper = hitEl.closest('.layer-wrapper--group');
      if (!groupWrapper || !groupWrapper.id || !groupWrapper.id.endsWith('#layerId')) {
        return false;
      }

      const groupId = groupWrapper.id.replace('#layerId', '');
      const group   = this.findGroupByIdAnywhere(groupId);
      if (!group) {
        return false;
      }

      const layerId = this.dragContext.layerId;
      if (!layerId || layerId === groupId) {
        return false;
      }

      const layers = this.getDraggedLayers();
      if (!layers.length || layers.some(layer => layer.type === 'group')) {
        return false;
      }

      return this.moveLayersIntoGroup(group, layers);
    },
    moveLayersIntoGroup(group, layers) {
      if (!this.findLayerLocation(group.Id)) {
        return false;
      }

      const movedLayers  = [];
      const sourceGroups = [];
      layers.forEach((layer) => {
        const location = this.findLayerLocation(layer.Id);
        if (!location || location.parent === group) { return; }
        movedLayers.push(location.list.splice(location.index, 1)[0]);
        if (location.parent && !sourceGroups.includes(location.parent)) {
          sourceGroups.push(location.parent);
        }
      });
      if (!movedLayers.length) {
        return false;
      }

      const target       = this.findLayerLocation(group.Id);
      const updatedGroup = addLayersToExistingGroup(target.list[target.index], movedLayers);
      this.$set(target.list, target.index, updatedGroup);
      sourceGroups.forEach(sourceGroup => this.updateGroupAfterChildRemoved(sourceGroup));

      this.dragContext = { fromGroup: null, layerId: null, selectedIds: [] };
      this.syncLayerChanges('layer drop into group');
      EventBus.$emit('onObjectSelectedFromRightPanel', updatedGroup.Id);
      return true;
    },
    onDocumentContextMenu(e) {
      if (!this.$el.contains(e.target)) {
        this.closeContextMenu();
      }
    },
    openContextMenu(event, layer, parentGroup = null, isChild = false) {
      if (!layer.isSelected) {
        this.clearAllLayerSelection();
        layer.isSelected = true;
      }

      this.contextMenu = {
        visible: true,
        x:       event.clientX,
        y:       event.clientY,
        layer,
        parentGroup,
        isChild,
      };

      this.$nextTick(this.keepContextMenuInViewport);
    },
    keepContextMenuInViewport() {
      const menu = this.$refs.contextMenu.$el;
      if (!menu) { return; }

      const { innerWidth, innerHeight } = window;
      const rect                        = menu.getBoundingClientRect();

      if (rect.right > innerWidth) {
        this.contextMenu.x -= rect.right - innerWidth;
      }

      if (rect.bottom > innerHeight) {
        this.contextMenu.y -= rect.bottom - innerHeight;
      }
    },
    closeContextMenu() {
      this.contextMenu.visible = false;
    },
    onContextMenuSelect(actionId) {
      const { layer, parentGroup, isChild } = this.contextMenu;
      console.log('this.contextMenu', this.contextMenu);
      switch (actionId) {

        case 'visibility':
          this.toggleVisibility(layer);
          break;

        case 'lock':
          this.lockLayer(layer);
          break;

        case 'duplicate':
          this.duplicateLayer(layer);
          break;

        case 'delete':
          this.handleDeleteLayer(layer, parentGroup, isChild);
          break;

        case 'add-to-group':
          this.addToGroup(layer);
          break;

        case 'add-to-this-group':
          this.addSelectionToGroup(layer);
          break;

        case 'ungroup': {
          const selectedGroups = this.layerList.filter(item => item.isSelected && item.type === 'group');
          if (!selectedGroups.length || !selectedGroups.some(g => g.Id === layer.Id)) {
            this.clearAllLayerSelection();
            layer.isSelected = true;
          }
          EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.layerList);
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UNGROUP_OBJECTS);
          break;
        }

        case 'remove-from-group':
          this.removeFromGroup(layer);
          break;
        default:
          break;
      }

      this.closeContextMenu();
    },
    getSelectedTopLevelLayers() {
      return this.layerList.filter(item => item.isSelected && item.type !== 'pagedetails' && item.type !== 'group');
    },
    getSelectedGroupLayer() {
      const selectedGroup = this.layerList.find(item => item.isSelected && item.type === 'group');
      if (selectedGroup) { return selectedGroup; }

      for (let i = 0; i < this.layerList.length; i += 1) {
        const group = this.layerList[i];
        if (group.type === 'group' && group.children && group.children.some(child => child.isSelected)) {
          return group;
        }
      }

      return null;
    },
    getLayersForGrouping(targetLayer) {
      const selected = this.getSelectedTopLevelLayers();
      if (selected.length) {
        return selected.some(item => item.Id === targetLayer.Id)
            ? selected
            : [...selected, targetLayer];
      }
      return [targetLayer];
    },
    addToGroup(targetLayer) {
      const selectedGroup = this.getSelectedGroupLayer();
      if (selectedGroup) {
        this.addSelectionToGroup(selectedGroup);
        return;
      }

      const layersToGroup = this.getLayersForGrouping(targetLayer);
      if (!canAddLayersToGroup(layersToGroup)) { return; }

      // Preserve layer-stack order for children; place group at first-selected layer
      const idsToGroup         = new Set(layersToGroup.map(item => item.Id));
      const layersInStackOrder = this.layerList.filter(item => idsToGroup.has(item.Id));

      const group = createGroupFromObjects(layersInStackOrder, this.layerList);
      this.$set(group, 'isGroupExpanded', true);

      const anchorId = (this.selectionAnchorId && idsToGroup.has(this.selectionAnchorId))
          ? this.selectionAnchorId
          : layersInStackOrder[0] && layersInStackOrder[0].Id;

      replaceLayersWithGroup(this.layerList, layersInStackOrder, group, anchorId);

      this.layerList.forEach((item) => { item.isSelected = false; });
      group.isSelected       = true;
      this.selectionAnchorId = group.Id;

      this.syncLayerChanges('called by addToGroup');
      EventBus.$emit('onObjectSelectedFromRightPanel', group.Id);
    },
    addSelectionToGroup(groupLayer) {
      const selected = this.getSelectedTopLevelLayers().filter(item => item.Id !== groupLayer.Id);
      if (!canAddLayersToGroup(selected)) { return; }

      const groupIndex = this.layerList.findIndex(item => item.Id === groupLayer.Id);
      if (groupIndex === -1) { return; }

      const updatedGroup = addLayersToExistingGroup(groupLayer, selected);
      const idsToRemove  = new Set(selected.map(item => item.Id));

      this.layerList[groupIndex] = updatedGroup;
      this.layerList             = this.layerList.filter(item => !idsToRemove.has(item.Id));

      this.syncLayerChanges('called by addSelectionToGroup');
      EventBus.$emit('onObjectSelectedFromRightPanel', updatedGroup.Id);
    },
    removeFromGroup(layer) {
      const layers       = this.getLayersForAction(layer).filter(item => !item.locked);
      const sourceGroups = [];

      // Bottom-up so the layers keep their order once inserted after the group
      layers.slice().reverse().forEach((item) => {
        const location = this.findLayerLocation(item.Id);
        if (!location || !location.parent) { return; }

        const group  = location.parent;
        const target = this.findLayerLocation(group.Id);
        if (!target) { return; }

        location.list.splice(location.index, 1);
        item.isSelected = false;
        target.list.splice(target.index + 1, 0, item);
        if (!sourceGroups.includes(group)) { sourceGroups.push(group); }
      });

      if (!sourceGroups.length) { return; }

      sourceGroups.forEach(group => this.updateGroupAfterChildRemoved(group));
      this.syncLayerChanges('called by removeFromGroup');
    },
    toggleGroupExpanded(group) {
      const next = group.isGroupExpanded === false;
      this.$set(group, 'isGroupExpanded', next);
    },
    syncLayerChanges(undoLabel) {
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.layerList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.emitUndoRedoRequest(undoLabel);
    },
    updateAudioLayerName(name, event) {
      const input = event && event.target;
      if (input && !input.readOnly && typeof input.setSelectionRange === 'function') {
        input.setSelectionRange(input.value.length, input.value.length);
      }
      this.isAudioLayerEditableForRightPanel = false;
      this.product.audioDetails.Name         = name;
    },
    selectAudioLayerFromLeftSection(value) {
      this.isAudioLayerSelected = value;
    },
    unSelectAudioLayer() {
      this.isAudioLayerEditableForRightPanel = false;
      this.isAudioLayerSelected              = false;
      EventBus.$emit('toggleAudioLeftSectionAccordion', false);
      this.$store.commit('activeProduct/UpdateIsAudioTrimOn', false);
    },
    makeAudioLayerEditableForRightPanel(event) {
      this.isAudioLayerEditableForRightPanel = true;
      const input = event && event.currentTarget;
      this.$nextTick(() => {
        if (!input) { return; }
        input.focus();
        const end = input.value.length;
        input.setSelectionRange(end, end);
      });
    },
    selectAudioLayer() {
      if (!this.isAudioLayerSelected) {
        EventBus.$emit('onObjectSelectedCleared');
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED);
        setTimeout(() => {
          this.isAudioLayerSelected = true;
          EventBus.$emit('toggleAudioLeftSectionAccordion', true);
        }, 10);
      }
    },
    removeAudioFromProduct() {
      EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CONFIRMATION_MODAL, {
        request: 'RemoveAudioFromProduct',
        message: 'Are you sure you want to delete this?',
      });
    },
    emitChangedList() {
      this.syncLayerChanges('called by swap layer');
    },
    emitUndoRedoRequest(data) {
      EventBus.$emit('addIntoPageWiseUndoRedoArray', data);
    },
    getAllSelectedLayers() {
      const selected = [];
      this.layerList.forEach((item) => {
        if (item.type === 'pagedetails') { return; }
        if (item.isSelected) {
          selected.push(item);
        }
        if (item.type === 'group' && item.children) {
          item.children.forEach((child) => {
            if (child.isSelected) {
              selected.push(child);
            }
          });
        }
      });
      return selected;
    },
    emitCurrentLayerSelection() {
      const selected = this.getAllSelectedLayers();
      if (selected.length > 1) {
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS, {
          objects:  selected,
          anchorId: this.selectionAnchorId,
        });
        return;
      }
      if (selected.length === 1) {
        EventBus.$emit('onObjectSelectedFromRightPanel', selected[0].Id);
        return;
      }
      this.selectionAnchorId = null;
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED);
    },
    selectLayer(layer, parentGroup = null, event = null) {
      this.closeContextMenu();
      const isMulti = !!(event && (event.shiftKey || event.ctrlKey || event.metaKey));

      if (!isMulti) {
        this.clearAllLayerSelection();
        layer.isSelected       = true;
        this.selectionAnchorId = layer.Id;
      } else {
        if (parentGroup) {
          parentGroup.isSelected = false;
        }
        if (layer.type === 'group' && layer.children) {
          layer.children.forEach((child) => { child.isSelected = false; });
        }

        const wasSelected = layer.isSelected;
        layer.isSelected  = !wasSelected;

        const selected = this.getSelectedLayers();
        if (!wasSelected) {
          if (selected.length === 1 || !this.selectionAnchorId) {
            this.selectionAnchorId = layer.Id;
          }
        } else if (this.selectionAnchorId === layer.Id) {
          this.selectionAnchorId = selected.length ? selected[0].Id : null;
        }
      }

      if (parentGroup && !isMulti) {
        this.$set(parentGroup, 'isGroupExpanded', true);
      }

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.layerList);

      const selectedLayers = this.getSelectedLayers();
      if (selectedLayers.length) {
        this.emitSelectionToCanvas(selectedLayers);
      } else {
        this.selectionAnchorId = null;
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED);
      }
      this.unSelectAudioLayer();
    },
    clearAllLayerSelection() {
      this.selectionAnchorId = null;
      clearAllSelection(this.layerList);
    },
    lockLayer(layer) {
      // The whole multi-selection when this layer belongs to one, else just it
      const layers = this.getLayersForAction(layer);

      layers.forEach((item) => {
        this.$set(item, 'locked', !item.locked);

        // Cascade down: If it's a group, sync all children to match the group's state
        if (item.type === 'group' && item.children) {
          item.children.forEach((child) => {
            this.$set(child, 'locked', item.locked);
          });
        }
      });

      // Cascade up: a group container is locked only while every child is
      this.getParentGroupsOf(layers).forEach((group) => {
        this.$set(group, 'locked', group.children.every(child => child.locked));
      });

      // Refresh Moveable/selection in place. Full canvas remount drops the active
      // target so unlocked group children won't move until the group is reselected.
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.layerList);
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
      this.emitUndoRedoRequest('called by lock layer');
    },
    toggleVisibility(layer) {
      // The whole multi-selection when this layer belongs to one, else just it
      const layers = this.getLayersForAction(layer);

      layers.forEach((item) => {
        item.IsVisible = !item.IsVisible;

        // Cascade down: If it's a group, hide/unhide all children to match the group
        if (item.type === 'group' && item.children) {
          item.children.forEach((child) => {
            child.IsVisible = item.IsVisible;
          });
        }
      });

      // Cascade up: a group container stays "visible" while a child still is
      // (This forces the parent icon to remain active/dot state)
      this.getParentGroupsOf(layers).forEach((group) => {
        group.IsVisible = group.children.some(child => child.IsVisible);
      });

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.layerList);
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);

      // Remount clears moveable; restore selection box for whatever is still selected and visible.
      const selectedVisible = this.getSelectedLayers().filter(item => item.IsVisible !== false);
      if (selectedVisible.length) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.emitSelectionToCanvas(selectedVisible);
          }, 50);
        });
      }

      this.emitUndoRedoRequest('called by hide layer');
    },
    duplicateLayer(object) {
      // The whole multi-selection when this layer belongs to one, else just it
      const layers       = this.getLayersForAction(object);
      const sourceGroups = [];

      layers.forEach((layer) => {
        const location = this.findLayerLocation(layer.Id);
        if (!location) { return; }

        const clone = this.createObjectCloneForDuplication(layer, this.activePage);
        // A child's copy stays beside it in the group; a root layer's goes on top.
        if (location.parent) {
          location.list.splice(location.index + 1, 0, clone);
          if (!sourceGroups.includes(location.parent)) { sourceGroups.push(location.parent); }
        } else {
          this.layerList.unshift(clone);
        }
      });

      sourceGroups.forEach(group => this.recalculateGroupBounds(group));

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.layerList);
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
      this.emitUndoRedoRequest('called by duplicateLayer');
    },
    handleDeleteLayer(layer, parentGroup = null, isChild = false) {
      // A multi-selection is deleted as one: the canvas drops every selected layer, group children included, so it takes the same path at any depth.
      if (this.getLayersForAction(layer).length > 1) {
        this.conformationMessagePopUp(layer, -1);
        return;
      }

      if (isChild && parentGroup) {
        this.conformationMessagePopUp(layer, {
          type:    'groupChild',
          groupId: parentGroup.Id,
          childId: layer.Id,
        });
        return;
      }

      const index = this.layerList.findIndex(item => item.Id === layer.Id);
      this.conformationMessagePopUp(layer, index);
    },
    conformationMessagePopUp(layer, index) {
      if (index && index.type === 'groupChild') {
        !layer.locked && EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CONFIRMATION_MODAL, {
          request: 'DeleteReq',
          data:    index,
          message: 'Are you sure you want to delete selected Layer(s)?',
        });
        return;
      }

      if (layer.isSelected) {
        const deletedLayersName = this.getSelectedLayers().map(item => item.Name).join(', ');
        EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CONFIRMATION_MODAL, {
          request: 'DeleteReq',
          data:    -1,
          message: 'Are you sure you want to delete selected Layer(s)?',
          isVideo: true,
        });
      } else {
        !layer.locked && EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CONFIRMATION_MODAL, {
          request: 'DeleteReq',
          data:    index,
          message: 'Are you sure you want to delete selected Layer(s)?',
        });
      }
    },
    createObjectCloneForDuplication(item, activePageNo) {
      const clone = item.type === 'group'
          ? reassignGroupIds(item)
          : _.cloneDeep(item);

      if (clone.type !== 'group') {
        clone.Id = uuid.v1();
      }

      clone.X         += 30;
      clone.Y         += 30;
      clone.isSelected = false;
      clone.editor     = null;
      clone.PageNo     = activePageNo;

      if (clone.type === 'table') {
        clone.Name = getNextTableName(this.layerList);
      } else if (clone.type === 'group') {
        clone.Name = getUniqueGroupName(this.layerList, 'Group');
        if (Array.isArray(clone.children)) {
          clone.children.forEach((child) => { child.isSelected = false; });
        }
      } else {
        let nextName = `${clone.Name || 'Layer'} copy`;
        while (this.layerList.find(layer => layer.Name === nextName)) {
          nextName = `${nextName} copy`;
        }
        clone.Name = nextName;
      }

      if (clone.type === 'text' && clone.modifiedHtml !== clone.Value) {
        clone.Value = clone.modifiedHtml;
      }

      return clone;
    },
    validateInput(evt) {
      if (!evt.target.readOnly) {
        evt.stopPropagation();
      }
      const code = (evt.which) ? evt.which : evt.keyCode;
      if ([8, 37, 38, 39, 40, 46].indexOf(code) > -1) {
        return true;
      }
      if ((code >= 65 && code <= 90) || (code >= 48 && code <= 57 && !evt.shiftKey) || (code >= 96 && code <= 105 && !evt.shiftKey) || code == 109 || code == 32 || code == 189) {
        return true;
      }
      evt.preventDefault();
      return false;
    },
  },

};

</script>
<style lang="scss">
.layer-main-wrapper {
  .layers-heading {
    padding: 18px 14px 6px;
  }

  .sortable-ghost {
    background: transparent !important;
    height: 0 !important;
    min-height: 0 !important;
    padding: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    overflow: visible !important;
    position: relative;
    border: none !important;
    box-shadow: none !important;

    > * {
      display: none !important;
    }

    &::after {
      content: "";
      position: absolute;
      top: -1px;
      left: 8px;
      right: 6px;
      height: 2px;
      border-radius: 1px;
      background: #1570EF;
      z-index: 10;
    }

    &::before {
      content: "";
      position: absolute;
      top: -3px;
      left: 5px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #1570EF;
      box-shadow: 0 0 0 2px #fff;
      z-index: 11;
      display: block !important;
    }
  }
}

.layer-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 8px;
}

.layer-audio-section {
  margin-top: 12px;
}

.layer-section-divider {
  height: 1px;
  margin: 0 14px 6px;
  background: #E7E9ED;
}

.layer-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 6px 0 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #3D4350;

  &:focus {
    outline: none;
  }
}

.layer-chevron-slot {
  width: 16px;
  flex-shrink: 0;
}

.layer-chevron-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #5E6573;
  cursor: pointer;

  &:focus {
    outline: none;
    border: 0;
  }

  &:focus-visible {
    outline: 2px solid #1570EF;
    outline-offset: 1px;
  }
}

.layer-type-icon {
  display: flex;
  flex-shrink: 0;
  color: #3D4350;
}

.layer-input {
  flex-grow: 1;
  min-width: 0;
  width: auto;
  height: 26px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: #14161B;
  text-overflow: ellipsis;
  text-transform: capitalize;
  outline: 0;
  box-shadow: none;
  cursor: default;

  &:focus {
    outline: none;
    border: 0;
  }

  &:not(:focus)::selection,
  &:not(:focus)::-moz-selection {
    background: transparent;
    color: inherit;
  }
}

.layer-input--editing {
  box-sizing: border-box;
  height: 26px;
  margin-left: -3px;
  padding: 0 7px;
  border: 1.5px solid #1570EF;
  border-radius: 6px;
  background: #fff;
  font-weight: 500;
  color: #14161B;
  text-transform: none;
  box-shadow: 0 0 0 3px rgba(21, 112, 239, 0.15);
  cursor: text;

  &:focus {
    border: 1.5px solid #1570EF;
    outline: none;
  }
}

.layer-actions {
  display: flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
}

.layer-action {
  display: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #3D4350;
  cursor: pointer;

  &:hover {
    background: #F3F4F6;
  }

  &:focus {
    outline: none;
    border: 0;
  }

  &:focus-visible {
    outline: 2px solid #1570EF;
    outline-offset: 1px;
  }
}

.layer-action--persist {
  display: flex;
  background: #EAF2FE;
  color: #0B57C2;

  &:hover {
    background: #D6E6FC;
  }
}

.layer-wrapper:hover,
.layer-wrapper.active-layer,
.layer-wrapper.layer-wrapper--in-group-selection {
  .layer-action {
    display: flex;
  }
}

.layer-wrapper:hover:not(.active-layer):not(.layer-wrapper--renaming):not(.layer-wrapper--drop-target) {
  background: #FFFFFF;
  box-shadow: 0 0 0 1px #E7E9ED, 0 1px 3px rgba(16, 24, 40, 0.08);
}

.layer-wrapper.active-layer,
.layer-wrapper--renaming {
  background: #EAF2FE;
  box-shadow: inset 0 0 0 1px #A9C9FA;

  .layer-type-icon {
    color: #0B57C2;
  }

  .layer-input:not(.layer-input--editing) {
    font-weight: 600;
  }
}

.layer-wrapper--in-group-selection:not(:hover) {
  background: #F4F8FE;
  box-shadow: none;
}

.layer-wrapper--group .layer-input:not(.layer-input--editing) {
  font-weight: 600;
}

.layer-wrapper--hidden:not(.layer-wrapper--renaming) {
  .layer-input:not(.layer-input--editing) {
    color: #9AA0AA;
    text-decoration: line-through;
    text-decoration-color: #D5D9E0;
  }
}

.layer-wrapper--hidden:not(.layer-wrapper--renaming):not(.active-layer) .layer-type-icon {
  color: #9AA0AA;
}

.layer-wrapper--renaming .layer-actions {
  display: none;
}

.layer-wrapper--drop-target {
  background: #EAF2FE;
  box-shadow: inset 0 0 0 1.5px #1570EF;

  .layer-type-icon {
    color: #0B57C2;
  }
}

.layer-group-children {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:not(.layer-group-children--collapsed)::before {
    content: "";
    position: absolute;
    left: 15px;
    top: 2px;
    bottom: 2px;
    width: 1px;
    pointer-events: none;
  }

  &--collapsed {
    height: 6px;
    min-height: 6px;
    overflow: hidden;
  }
}

.layer-drag-preview {
  position: fixed;
  top: -1000px;
  left: -1000px;
  width: 190px;
  height: 64px;
  pointer-events: none;

  &__back,
  &__mid,
  &__front {
    position: absolute;
    width: 168px;
    height: 36px;
    border-radius: 9px;
    background: #fff;
    box-shadow: 0 0 0 1px #E7E9ED, 0 4px 12px rgba(16, 24, 40, 0.1);
  }

  &__back {
    left: 10px;
    top: 10px;
    transform: rotate(3deg);
    z-index: 1;
  }

  &__mid {
    left: 5px;
    top: 5px;
    transform: rotate(1deg);
    z-index: 2;
  }

  &__front {
    left: 0;
    top: 0;
    z-index: 3;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px 0 10px;
    box-shadow: 0 0 0 1.5px #1570EF, 0 10px 24px rgba(16, 24, 40, 0.18);
    transform: rotate(-2deg);
    color: #0B57C2;
  }

  &__name {
    flex-grow: 1;
    overflow: hidden;
    font-size: 12.5px;
    font-weight: 600;
    color: #14161B;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    box-sizing: border-box;
    border-radius: 10px;
    background: #1570EF;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
  }
}

.layer-main-wrapper input {
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sortable-drag {
  opacity: 0.8;
}

.layer-row-block {
  position: relative;
}

.layer-row-block.sortable-ghost::before {
  display: block !important;
}

</style>
